import { ethers } from "ethers";
import getPublicSigner from "./publicSigner";
import settlerJSON from "./settlerContract.json";
import polygonCollectorJSON from "./polygonCollectorContract.json";
import ParticipantsList from "./participantsList";
import Participant from "./participant";
import { decimalAdjust } from "app/functions/numbers";
import { getNetworkByChainId, SETTLEMENT_CHAIN } from "../config/networks";
import { getToken, isSettlementToken } from "../config/tokens";

const { publicSigner, publicprovider } = getPublicSigner();

// ============ CONTRACT CONFIGURATION ============
// Get contract addresses from networks.ts
const getContracts = (chainId) => {
  const network = getNetworkByChainId(chainId);
  if (!network) return null;

  return {
    settler: network.contracts.settler || "",
    collector: network.contracts.collector || "",
    usdt: network.contracts.usdt,
    wmatic: network.contracts.wmatic,
    swapRouter: network.contracts.swapRouter,
    isSettlementChain: network.isSettlementChain,
  };
};

// Settlement chain config
const SETTLER_ADDRESS = SETTLEMENT_CHAIN.contracts.settler || "";
const COLLECTOR_ADDRESS = SETTLEMENT_CHAIN.contracts.collector || "";

/**
 * Betting class - handles all betting operations
 *
 * Architecture:
 * - BettingSettler: Main contract that holds all bets (USDT only)
 * - PolygonCollector: Accepts any token, swaps to USDT, takes 1% fee (except USDT), calls settler
 *
 * Fee Structure:
 * - USDT bets: NO fee (goes directly to settler)
 * - All other tokens: 1% exchange fee (handled by collector after swap)
 * - All bets: 10% platform fee on winnings (handled by settler at claim)
 */
class Betting {
  async build() {
    const signer = publicSigner;
    this.iface = new ethers.Interface(settlerJSON.abi);

    if (!SETTLER_ADDRESS) {
      console.log("Warning: Settler contract not yet deployed");
      this.balance = 0;
      return this;
    }

    this.contract = new ethers.Contract(
      SETTLER_ADDRESS,
      settlerJSON.abi,
      signer
    );

    // Read totalPool from settler contract (USDT with 6 decimals)
    try {
      const pool = await this.contract.totalPool();
      this.balance = decimalAdjust(
        "ceil",
        ethers.formatUnits(pool.toString(), 6),
        -2
      );
    } catch (e) {
      console.log("Error reading pool:", e);
      this.balance = 0;
    }

    return this;
  }

  async getBalance() {
    if (!this.contract) return 0;
    try {
      const pool = await this.contract.totalPool();
      const amount = decimalAdjust(
        "ceil",
        ethers.formatUnits(pool.toString(), 6),
        -2
      );
      this.balance = amount;
      return amount;
    } catch (e) {
      console.log("Error reading pool:", e);
      return 0;
    }
  }

  async setSigner(signer) {
    if (!SETTLER_ADDRESS) return;
    this.contract = new ethers.Contract(
      SETTLER_ADDRESS,
      settlerJSON.abi,
      signer
    );
  }

  wait(transaction) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        const tran = await publicprovider.getTransaction(transaction);
        const confirmations = await tran?.confirmations?.() || 0;
        if (confirmations > 0) resolve(tran);
        else {
          await this.wait(transaction);
          resolve(tran);
        }
      }, 5000);
    });
  }

  async getWinnerBet() {
    if (!this.contract) return {};
    try {
      const winnerSet = await this.contract.winnerSet();
      if (winnerSet) {
        const winner = await this.contract.winner();
        const odds = await this.contract.getOdds(winner);
        const ratio = Number(odds) / 1e18;
        return { winner, ratio };
      }
    } catch (e) {
      console.log("Error getting winner:", e);
    }
    return {};
  }

  async getWinRatio() {
    if (!this.contract) return 0;
    try {
      const winnerSet = await this.contract.winnerSet();
      if (winnerSet) {
        const winner = await this.contract.winner();
        const odds = await this.contract.getOdds(winner);
        return Number(odds) / 1e18;
      }
    } catch (e) {
      console.log("Error getting ratio:", e);
    }
    return 0;
  }

  async isActive() {
    if (!this.contract) return false;
    try {
      return await this.contract.bettingActive();
    } catch (e) {
      console.log("Error checking active:", e);
      return false;
    }
  }

  async getparticipants() {
    const participantList = new ParticipantsList();
    if (!this.contract) return { participants: participantList, total: 0 };

    // Multicall3 contract on Polygon
    const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
    const multicallAbi = [
      "function aggregate3(tuple(address target, bool allowFailure, bytes callData)[] calls) view returns (tuple(bool success, bytes returnData)[])",
    ];

    try {
      const totalBets = await this.contract.getTotalBets();
      const total = Number(totalBets);

      if (total === 0) {
        return { participants: participantList, total: 0 };
      }

      // Fetch latest 100 bets (from newest to oldest)
      const startIndex = Math.max(0, total - 100);
      const endIndex = total;

      const multicall = new ethers.Contract(MULTICALL3_ADDRESS, multicallAbi, publicprovider);
      const contractAddress = await this.contract.getAddress();

      // Step 1: Fetch betIds using multicall
      const betIdCalls = [];
      for (let i = startIndex; i < endIndex; i++) {
        betIdCalls.push({
          target: contractAddress,
          allowFailure: true,
          callData: this.iface.encodeFunctionData("allBetIds", [i]),
        });
      }

      const betIdResults = await multicall.aggregate3(betIdCalls);
      const betIds = [];
      for (const result of betIdResults) {
        if (result.success) {
          const decoded = this.iface.decodeFunctionResult("allBetIds", result.returnData);
          betIds.push(decoded[0]);
        }
      }

      if (betIds.length === 0) {
        return { participants: participantList, total };
      }

      // Step 2: Fetch bet details using multicall
      const detailCalls = betIds.map((betId) => ({
        target: contractAddress,
        allowFailure: true,
        callData: this.iface.encodeFunctionData("getBetDetails", [betId]),
      }));

      const detailResults = await multicall.aggregate3(detailCalls);

      // Step 3: Build participant list (reverse order for newest first)
      for (let i = detailResults.length - 1; i >= 0; i--) {
        const result = detailResults[i];
        if (result.success) {
          try {
            const decoded = this.iface.decodeFunctionResult("getBetDetails", result.returnData);
            const bet = decoded[0] || decoded;
            const amount = ethers.formatUnits(bet.amount.toString(), 6);
            participantList.set(
              new Participant({
                amount: amount,
                wallet: bet.bettor,
                bet: bet.countryCode,
                betId: betIds[i],
              })
            );
          } catch (e) {
            // Skip malformed bet data
          }
        }
      }

      return { participants: participantList, total };
    } catch (e) {
      console.log("Error getting participants:", e);
      return { participants: participantList, total: 0 };
    }
  }

  async getparticipant(wallet) {
    if (!this.contract) {
      return new Participant({ amount: "0", wallet: wallet, bet: "" });
    }

    try {
      const betIds = await this.contract.getUserBets(wallet);
      if (betIds.length > 0) {
        const bet = await this.contract.getBetDetails(betIds[0]);
        const amount = ethers.formatUnits(bet.amount.toString(), 6);
        return new Participant({
          amount: amount,
          wallet: bet.bettor,
          bet: bet.countryCode,
          betId: betIds[0],
        });
      }
    } catch (e) {
      console.log("Error getting participant:", e);
    }
    return new Participant({ amount: "0", wallet: wallet, bet: "" });
  }

  /**
   * Get all bets for a wallet
   * @param wallet - The wallet address
   * @returns Array of bet objects
   */
  async getAllUserBets(wallet) {
    if (!this.contract) {
      return [];
    }

    try {
      const betIds = await this.contract.getUserBets(wallet);
      const bets = [];

      for (const betId of betIds) {
        const bet = await this.contract.getBetDetails(betId);
        bets.push({
          betId: betId,
          wallet: bet.bettor,
          countryCode: bet.countryCode,
          amount: ethers.formatUnits(bet.amount.toString(), 6),
          timestamp: Number(bet.timestamp),
          claimed: bet.claimed,
        });
      }

      return bets;
    } catch (e) {
      console.log("Error getting user bets:", e);
      return [];
    }
  }

  /**
   * Place a bet using the PolygonCollector
   *
   * @param provider - Web3 provider (may be stale after network switch)
   * @param code - Country code to bet on
   * @param amount - Amount to bet (in token's native units)
   * @param tokenSymbol - Token symbol (USDT, POL, LINK, etc.)
   */
  async placeBet(provider, code, amount, tokenSymbol = "USDT") {
    // Get fresh provider from window.ethereum to avoid stale provider after network switch
    const ethereum = window.ethereum;
    if (!ethereum) {
      throw new Error("No wallet detected. Please install MetaMask or another Web3 wallet.");
    }

    const freshProvider = new ethers.BrowserProvider(ethereum);
    const signer = await freshProvider.getSigner();
    const network = await freshProvider.getNetwork();
    const chainId = Number(network.chainId);

    // Verify we're on Polygon
    if (chainId !== 137) {
      throw new Error("Please switch to Polygon network to place bets. Current chain: " + chainId);
    }

    const chainConfig = getContracts(chainId);
    if (!chainConfig) {
      throw new Error("Network configuration not found");
    }

    // Check if collector is deployed
    if (!chainConfig.collector) {
      throw new Error("PolygonCollector contract not yet deployed");
    }

    // Get token configuration
    const tokenConfig = getToken(chainId, tokenSymbol);
    if (!tokenConfig) {
      throw new Error(`Token ${tokenSymbol} not supported`);
    }

    // Create collector contract instance
    const collectorContract = new ethers.Contract(
      chainConfig.collector,
      polygonCollectorJSON.abi,
      signer
    );

    // Handle based on token type
    if (tokenConfig.address === "native") {
      // Native POL - use betWithNative
      return this._betWithNative(collectorContract, code, amount);
    } else if (isSettlementToken(tokenSymbol)) {
      // USDT - use betWithUSDT (no fee)
      return this._betWithUSDT(collectorContract, chainConfig, code, amount, signer);
    } else {
      // Other ERC-20 - use betWithToken (1% fee)
      return this._betWithToken(collectorContract, code, amount, tokenConfig, signer);
    }
  }

  /**
   * Bet with native POL token
   */
  async _betWithNative(collectorContract, code, amount) {
    const _amount = ethers.parseUnits(amount.toString(), 18);

    console.log(`Betting ${amount} POL on ${code}...`);
    const transaction = await collectorContract.betWithNative(code, {
      value: _amount,
    });

    return transaction;
  }

  /**
   * Bet with USDT (no exchange fee)
   */
  async _betWithUSDT(collectorContract, chainConfig, code, amount, signer) {
    const _amount = ethers.parseUnits(amount.toString(), 6); // USDT has 6 decimals

    // Approve collector to spend USDT
    const usdtContract = new ethers.Contract(
      chainConfig.usdt,
      [
        "function approve(address spender, uint256 amount) returns (bool)",
        "function allowance(address owner, address spender) view returns (uint256)",
      ],
      signer
    );

    // Check current allowance
    const signerAddress = await signer.getAddress();
    const collectorAddress = await collectorContract.getAddress();
    const currentAllowance = await usdtContract.allowance(signerAddress, collectorAddress);

    // Only approve if needed
    if (currentAllowance < _amount) {
      console.log("Approving USDT...");
      const approveTx = await usdtContract.approve(collectorAddress, _amount);
      await approveTx.wait();
    }

    // Place bet - NO FEE for USDT
    console.log(`Betting ${amount} USDT on ${code} (no fee)...`);
    const transaction = await collectorContract.betWithUSDT(_amount, code);

    return transaction;
  }

  /**
   * Bet with any ERC-20 token (1% exchange fee)
   */
  async _betWithToken(collectorContract, code, amount, tokenConfig, signer) {
    const _amount = ethers.parseUnits(amount.toString(), tokenConfig.decimals);

    // Approve collector to spend the token
    const tokenContract = new ethers.Contract(
      tokenConfig.address,
      [
        "function approve(address spender, uint256 amount) returns (bool)",
        "function allowance(address owner, address spender) view returns (uint256)",
      ],
      signer
    );

    // Check current allowance
    const signerAddress = await signer.getAddress();
    const collectorAddress = await collectorContract.getAddress();
    const currentAllowance = await tokenContract.allowance(signerAddress, collectorAddress);

    // Only approve if needed
    if (currentAllowance < _amount) {
      console.log(`Approving ${tokenConfig.symbol}...`);
      const approveTx = await tokenContract.approve(collectorAddress, _amount);
      await approveTx.wait();
    }

    // Place bet - 1% exchange fee will be taken by collector
    console.log(`Betting ${amount} ${tokenConfig.symbol} on ${code} (1% fee)...`);
    const transaction = await collectorContract.betWithToken(
      tokenConfig.address,
      _amount,
      code
    );

    return transaction;
  }

  /**
   * Get quote for betting with a specific token
   * Returns expected USDT amount after swap and fees
   */
  async getQuote(tokenSymbol, amount) {
    if (!COLLECTOR_ADDRESS) {
      return { amountOut: 0, fee: 0, netAmount: 0 };
    }

    const tokenConfig = getToken(137, tokenSymbol);
    if (!tokenConfig) {
      return { amountOut: 0, fee: 0, netAmount: 0 };
    }

    try {
      const collectorContract = new ethers.Contract(
        COLLECTOR_ADDRESS,
        polygonCollectorJSON.abi,
        publicSigner
      );

      const _amount = ethers.parseUnits(amount.toString(), tokenConfig.decimals);

      if (tokenConfig.address === "native") {
        const [amountOut, fee, netAmount] = await collectorContract.getQuoteNative(_amount);
        return {
          amountOut: ethers.formatUnits(amountOut, 6),
          fee: ethers.formatUnits(fee, 6),
          netAmount: ethers.formatUnits(netAmount, 6),
        };
      } else {
        const [amountOut, fee, netAmount] = await collectorContract.getQuote(
          tokenConfig.address,
          _amount
        );
        return {
          amountOut: ethers.formatUnits(amountOut, 6),
          fee: ethers.formatUnits(fee, 6),
          netAmount: ethers.formatUnits(netAmount, 6),
        };
      }
    } catch (e) {
      console.log("Error getting quote:", e);
      return { amountOut: 0, fee: 0, netAmount: 0 };
    }
  }

  async claim(provider) {
    if (!SETTLER_ADDRESS) {
      throw new Error("Settler contract not yet deployed");
    }

    // Get fresh provider from window.ethereum to avoid stale provider after network switch
    const ethereum = window.ethereum;
    if (!ethereum) {
      throw new Error("No wallet detected. Please install MetaMask or another Web3 wallet.");
    }

    const freshProvider = new ethers.BrowserProvider(ethereum);
    const signer = await freshProvider.getSigner();
    await this.setSigner(signer);

    // Use claimAll to claim all winning bets
    const transaction = await this.contract.claimAll();

    return transaction;
  }

  /**
   * Get all countries with enabled status from the contract
   * @returns {Promise<{code: string, enabled: boolean}[]>}
   */
  async getAllCountries() {
    if (!this.contract) return [];

    try {
      const [codes, enabled] = await this.contract.getAllCountries();
      return codes.map((code, i) => ({
        code: code,
        enabled: enabled[i]
      }));
    } catch (e) {
      console.log("Error getting countries:", e);
      return [];
    }
  }

  /**
   * Get total USDT bet on a specific country
   */
  async getCountryTotal(countryCode) {
    if (!this.contract) return 0;
    try {
      const total = await this.contract.getCountryTotal(countryCode);
      return parseFloat(ethers.formatUnits(total, 6));
    } catch (e) {
      console.log("Error getting country total:", e);
      return 0;
    }
  }

  /**
   * Get odds/multiplier for a specific country
   */
  async getOdds(countryCode) {
    if (!this.contract) return 0;
    try {
      const odds = await this.contract.getOdds(countryCode);
      return Number(odds) / 1e18;
    } catch (e) {
      console.log("Error getting odds:", e);
      return 0;
    }
  }

  /**
   * Get stats for all enabled countries using multicall
   * @returns Array of {code, total, odds, poolShare}
   */
  async getAllCountryStats() {
    if (!this.contract) return [];

    const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";
    const multicallAbi = [
      "function aggregate3(tuple(address target, bool allowFailure, bytes callData)[] calls) view returns (tuple(bool success, bytes returnData)[])",
    ];

    try {
      const [codes, enabled] = await this.contract.getAllCountries();
      const enabledCodes = codes.filter((_, i) => enabled[i]);

      if (enabledCodes.length === 0) return [];

      const multicall = new ethers.Contract(MULTICALL3_ADDRESS, multicallAbi, publicprovider);
      const contractAddress = await this.contract.getAddress();

      // Build multicall for totals and odds
      const calls = [];
      enabledCodes.forEach(code => {
        calls.push({
          target: contractAddress,
          allowFailure: true,
          callData: this.iface.encodeFunctionData("getCountryTotal", [code]),
        });
        calls.push({
          target: contractAddress,
          allowFailure: true,
          callData: this.iface.encodeFunctionData("getOdds", [code]),
        });
      });

      const results = await multicall.aggregate3(calls);

      const stats = [];
      const totalPool = this.balance || 0;

      for (let i = 0; i < enabledCodes.length; i++) {
        const totalResult = results[i * 2];
        const oddsResult = results[i * 2 + 1];

        let total = 0;
        let oddsDecimal = 0;

        if (totalResult.success) {
          const decoded = this.iface.decodeFunctionResult("getCountryTotal", totalResult.returnData);
          total = parseFloat(ethers.formatUnits(decoded[0], 6));
        }

        if (oddsResult.success) {
          const decoded = this.iface.decodeFunctionResult("getOdds", oddsResult.returnData);
          oddsDecimal = Number(decoded[0]) / 1e18;
        }

        const poolShare = totalPool > 0 ? (total / totalPool) * 100 : 0;

        stats.push({
          code: enabledCodes[i],
          total,
          odds: oddsDecimal,
          poolShare,
        });
      }

      return stats;
    } catch (e) {
      console.log("Error getting country stats:", e);
      return [];
    }
  }

  /**
   * Get recent BetPlaced events for live activity feed
   * @param blocksBack - How many blocks back to query (default 500 ~= ~16 minutes)
   * @returns Array of {betId, bettor, countryCode, amount, timestamp}
   */
  async getRecentBetEvents(blocksBack = 500) {
    if (!this.contract) return [];

    try {
      const currentBlock = await publicprovider.getBlockNumber();
      const fromBlock = Math.max(0, currentBlock - blocksBack);

      const events = await this.contract.queryFilter("BetPlaced", fromBlock, currentBlock);

      return events.map(event => ({
        betId: event.args[0],
        bettor: event.args[1],
        countryCode: event.args[2],
        amount: parseFloat(ethers.formatUnits(event.args[3], 6)),
        timestamp: Number(event.args[5]) * 1000,
      })).reverse(); // newest first
    } catch (e) {
      console.log("Error getting recent events:", e);
      return [];
    }
  }

  onNewBet(fn) {
    if (!this.contract) return;

    // Poll for new events since public RPC doesn't support eth_newFilter
    let lastBlock = 0;

    const pollEvents = async () => {
      try {
        const currentBlock = await publicprovider.getBlockNumber();
        if (lastBlock === 0) {
          lastBlock = currentBlock;
          return;
        }

        if (currentBlock > lastBlock) {
          const events = await this.contract.queryFilter(
            "BetPlaced",
            lastBlock + 1,
            currentBlock
          );

          for (const event of events) {
            const bettor = event.args[1]; // bettor is second arg
            fn(bettor);
          }

          lastBlock = currentBlock;
        }
      } catch (e) {
        // Silently ignore polling errors
      }
    };

    // Poll every 10 seconds
    this.eventPollInterval = setInterval(pollEvents, 10000);
    pollEvents(); // Initial poll
  }
}

export default new Betting();
